import { TemplateRef } from '@angular/core';
import { SortDirection } from './sort-direction';

class BaseGridColumn {
  public type: ColumnType;
  public canSort = false;
  public canFilter = false;
  public alignToRight = false;
  public sortTitles: Record<SortDirection, string> = {
    [SortDirection.Ascending]: 'Sort A-Z',
    [SortDirection.Descending]: 'Sort Z-A'
  };
  public filter: ColumnFilter = {
    type: null,
    config: null
  };
  public customClass = '';

  constructor(public name: string, public title: string) {}

  public mapClassFn = _ => '';

  public addSorting(sortTitles?: Record<SortDirection, string>) {
    this.canSort = true;
    if (sortTitles) {
      this.sortTitles = sortTitles;
    }
    return this;
  }

  public addFiltration(columnFilterSettings: ColumnFilter) {
    this.canFilter = true;
    if (this.canFilter && (!columnFilterSettings || !columnFilterSettings.type || !columnFilterSettings.config)) {
      throw new Error('Filter settings is required if canFilter is true');
    } else {
      this.filter.type = columnFilterSettings.type;

      if (this.filter.type === ColumnFilterType.Options) {
        const { options, allowSearch } = columnFilterSettings.config as ColumnOptionsFilter;
        this.filter.config = new ColumnOptionsFilter(options, allowSearch);
      } else if (this.filter.type === ColumnFilterType.Typeahead) {
        const { query } = columnFilterSettings.config as ColumnTypeaheadFilter;
        this.filter.config = new ColumnTypeaheadFilter(query);
      }
    }
    return this;
  }

  public addClass(className) {
    this.customClass = className;
    return this;
  }

  public addMapClass(mapClassFn: (item: any) => string) {
    this.mapClassFn = mapClassFn;
    return this;
  }

  public setAlignToRight() {
    this.alignToRight = true;
    return this;
  }
}

export enum ColumnType {
  String = 1,
  Number = 2,
  Boolean = 3,
  Date = 4,
  DateTime = 5,
  Enum = 6,
  CustomTemplate = 7
}

export class StringColumn extends BaseGridColumn {
  constructor(name: string, title: string) {
    super(name, title);
    this.type = ColumnType.String;
  }
}

export class NumberColumn extends BaseGridColumn {
  constructor(name: string, title: string) {
    super(name, title);
    this.type = ColumnType.Number;
  }
}

export class BooleanColumn extends BaseGridColumn {
  constructor(name: string, title: string, public trueValue: string, public falseValue: string) {
    super(name, title);
    this.type = ColumnType.Boolean;
  }
}

export class DateColumn extends BaseGridColumn {
  constructor(name: string, title: string) {
    super(name, title);
    this.type = ColumnType.Date;
  }
}

export class DateTimeColumn extends BaseGridColumn {
  constructor(name: string, title: string) {
    super(name, title);
    this.type = ColumnType.DateTime;
  }
}

export class EnumColumn extends BaseGridColumn {
  constructor(name: string, title: string, public enumData: Record<string, any>) {
    super(name, title);
    this.type = ColumnType.Enum;
  }
}

export class CustomTemplateColumn extends BaseGridColumn {
  public template: TemplateRef<any>;
  constructor(name: string, title: string, template: TemplateRef<any>) {
    super(name, title);
    this.type = ColumnType.CustomTemplate;
    this.template = template;
  }
}

export type GridColumn =
  | StringColumn
  | DateColumn
  | DateTimeColumn
  | NumberColumn
  | BooleanColumn
  | EnumColumn
  | CustomTemplateColumn;

export enum ColumnFilterType {
  Options = 1,
  Typeahead = 2,
  Daterange = 3
}

export type ColumnFilterConfig = ColumnOptionsFilter | ColumnTypeaheadFilter | ColumnDaterangeFilter;

export interface ColumnFilter {
  type: ColumnFilterType;
  config: ColumnFilterConfig;
}

export class ColumnFilter implements ColumnFilter {
  constructor(public type: ColumnFilterType, public config: ColumnFilterConfig) {}
}

export interface OptionFilterItem {
  text: string;
  value: string | number | boolean;
}

export class ColumnOptionsFilter {
  constructor(public options: OptionFilterItem[], public allowSearch?: boolean) {}
}

export class ColumnTypeaheadFilter {
  constructor(public query: (searchText: string) => OptionFilterItem[]) {}
}

export class ColumnDaterangeFilter {
  constructor(public minDate?: string, public maxDate?: string) {}
}
